/**
 * Closet Section Component
 * Content tagged with #ootd
 * Mobile-first TikTok-native UX
 * Mobile: One item at a time, swipe up/left to advance, dot navigation
 * Desktop: Layered collage with hover-to-play previews (no first-item autoplay)
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import MediaEmbed from './MediaEmbed';
import { useSwipeGesture } from '../hooks/useSwipeGesture';
import { useAutoplay } from '../context/AutoplayContext';

// Skeleton loader component for loading states
const MediaSkeleton = ({ aspectRatio = '9/16' }) => (
  <div
    className="animate-pulse bg-gradient-to-br from-purple-100 to-pink-100 rounded-lg"
    style={{ aspectRatio }}
  >
    <div className="h-full flex flex-col items-center justify-center space-y-3">
      <div className="w-16 h-16 bg-purple-200 rounded-full"></div>
      <div className="w-24 h-3 bg-purple-200 rounded"></div>
      <div className="w-16 h-3 bg-purple-200 rounded"></div>
    </div>
  </div>
);

// Custom hook for lazy loading items based on visibility
const useLazyLoad = (items, visibleRange = 3) => {
  const [loadedIndices, setLoadedIndices] = useState(new Set([0, 1, 2]));

  const loadItem = useCallback((index) => {
    setLoadedIndices(prev => {
      const newSet = new Set(prev);
      // Load current item and items within range
      for (let i = Math.max(0, index - visibleRange); i <= Math.min(items.length - 1, index + visibleRange); i++) {
        newSet.add(i);
      }
      return newSet;
    });
  }, [items.length, visibleRange]);

  const isLoaded = useCallback((index) => loadedIndices.has(index), [loadedIndices]);

  return { loadItem, isLoaded };
};

const ClosetSection = ({ content = [] }) => {
  const [mobileIndex, setMobileIndex] = useState(0);
  const [hoveredItem, setHoveredItem] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const { shouldAutoplay } = useAutoplay();
  const isSectionAutoplay = shouldAutoplay('closet-anchor');
  const { loadItem, isLoaded } = useLazyLoad(content, 3);

  // Detect mobile/desktop for device-specific autoplay
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Load items around current mobile index
  useEffect(() => {
    loadItem(mobileIndex);
  }, [mobileIndex, loadItem]);

  const handleSwipe = (direction) => {
    if (direction === 'up' || direction === 'left') {
      setMobileIndex((prev) => Math.min(prev + 1, content.length - 1));
    } else if (direction === 'down' || direction === 'right') {
      setMobileIndex((prev) => Math.max(prev - 1, 0));
    }
  };

  // Standardized swipe distance of 40px for better UX
  const swipeHandlers = useSwipeGesture(handleSwipe, {
    minSwipeDistance: 40,
  });

  if (!content || content.length === 0) {
    return (
      <section
        id="closet-anchor"
        className="min-h-screen flex items-center justify-center bg-gradient-to-br from-pink-50 to-purple-50"
      >
        <div className="text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">The Closet</h2>
          <p className="text-gray-600">No content available yet</p>
        </div>
      </section>
    );
  }

  // Desktop autoplay logic: hover only (no first-item autoplay)
  const getDesktopAutoplay = (index, itemId) => {
    return hoveredItem === itemId;
  };

  // Mobile autoplay logic: first item only when section is in view
  const getMobileAutoplay = (index) => {
    return index === mobileIndex && index === 0 && isSectionAutoplay;
  };

  return (
    <section
      id="closet-anchor"
      className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50 py-12 md:py-20"
    >
      <div className="max-w-7xl mx-auto px-4">
        {/* Section header */}
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            The Closet 👗
          </h2>
          <p className="text-lg text-gray-600">
            Daily outfits, fashion inspiration, and style journey
          </p>
        </div>

        {/* Desktop: Improved grid with auto-flow dense */}
        <div className="hidden md:block">
          <div
            className="grid grid-cols-4 auto-rows-[200px] gap-4"
            style={{ gridAutoFlow: 'dense' }}
          >
            {content.slice(0, 12).map((item, index) => {
              const isHovered = hoveredItem === item.id;

              // Simplified grid patterns that work well with dense packing
              const getGridClass = () => {
                if (index === 0) return 'col-span-2 row-span-2'; // Hero item
                if (index === 1) return 'col-span-1 row-span-2'; // Tall item
                if (index === 3) return 'col-span-2 row-span-1'; // Wide item
                if (index === 5) return 'col-span-1 row-span-2'; // Another tall
                if (index === 8) return 'col-span-2 row-span-1'; // Another wide
                return 'col-span-1 row-span-1'; // Standard items
              };

              return (
                <div
                  key={item.id}
                  className={`
                    ${getGridClass()}
                    rounded-lg overflow-hidden shadow-lg
                    transform transition-all duration-300
                    ${isHovered ? 'scale-[1.02] z-10 shadow-2xl' : 'hover:shadow-xl'}
                  `}
                  onMouseEnter={() => {
                    setHoveredItem(item.id);
                    loadItem(index); // Lazy load on hover
                  }}
                  onMouseLeave={() => setHoveredItem(null)}
                >
                  {isLoaded(index) ? (
                    <MediaEmbed
                      item={item}
                      aspectRatio="9/16"
                      fillParent={true}
                      autoplay={getDesktopAutoplay(index, item.id)}
                      lazy={true}
                    />
                  ) : (
                    <MediaSkeleton aspectRatio="9/16" />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Mobile: Swipe queue with dot navigation (consistent with other sections) */}
        <div className="md:hidden relative" {...swipeHandlers}>
          <div className="aspect-[9/16] max-h-[70vh] mx-auto rounded-2xl overflow-hidden shadow-2xl">
            {content[mobileIndex] && (
              isLoaded(mobileIndex) ? (
                <MediaEmbed
                  item={content[mobileIndex]}
                  aspectRatio="9/16"
                  autoplay={getMobileAutoplay(mobileIndex)}
                  lazy={true}
                />
              ) : (
                <MediaSkeleton aspectRatio="9/16" />
              )
            )}
          </div>

          {/* Navigation dots (consistent with Gym and Office sections) */}
          <div className="flex justify-center gap-2 mt-6 flex-wrap px-4">
            {content.map((_, index) => (
              <button
                key={index}
                onClick={() => {
                  setMobileIndex(index);
                  loadItem(index);
                }}
                className={`
                  w-2 h-2 rounded-full transition-all
                  ${index === mobileIndex ? 'bg-purple-500 w-8' : 'bg-purple-200'}
                `}
                aria-label={`Go to item ${index + 1}`}
              />
            ))}
          </div>

          {/* Swipe hint */}
          <p className="text-center text-gray-500 text-sm mt-4">
            Swipe up or left for next
          </p>
        </div>
      </div>
    </section>
  );
};

export default ClosetSection;
